import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowLeft, DollarSign, Clock, FileText, Mail, Phone, AlertTriangle } from 'lucide-react';
import { StepCard, InfoCard } from './SectionComponents';

const SalesOffersEndgameSection = ({ onBack, onComplete, isCompleted, onNext, nextSectionTitle, hasNext }) => {

  const firstSteps = [
    'Let the Jumpstart team know! We can help you navigate the final steps and make sure you\'re set up for success. Email bethan at bethan@jumpstart-uk.com',
    'Thank the company',
    'Acknowledge the offer promptly',
    'Ask for a bit of time if you need it (though we would recommend no more than a few days!)'
  ];

  const negotiationReasons = [
    'The OTE feels below market for your experience',
    'You\'ve hit strong targets in a previous role',
    'You have another offer on the table'
  ];

  const clarificationPoints = [
    'Base salary vs. OTE',
    'Commission structure (flat %, tiered, quota-based?)',
    'Ramp-up period — is there a grace period for your first targets?'
  ];

  const contractChecklist = [
    'Base salary and realistic OTE — does it match expectations and market?',
    'Commission structure — when does it kick in?',
    'Ramp-up expectations — is there a grace period for targets?',
    'Line manager — is it who you expected?',
    'Holiday — would usually expect at least 20 days + bank holidays',
    'Pension — would usually expect a minimum 3% employer contribution',
    'Equity / options — vesting schedule and % or value clearly stated',
    'Probation — usually 3-6 months'
  ];

  const followUpTimeline = [
    {
      time: '48 hours',
      action: 'Send a polite follow-up'
    },
    {
      time: '3+ days',
      action: 'Let the Jumpstart team know by emailing info@jumpstart-uk.com'
    }
  ];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <div className="w-full py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12"
        >
          <div className="text-center mb-16">
            <h1 className="text-6xl font-bold text-gray-900 mb-6">
              Offers & what comes next
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Navigate the final steps from offer to signed contract with confidence.
            </p>
          </div>
          <div className="flex items-center justify-center space-x-6">
            <button
              onClick={onBack}
              className="inline-flex items-center px-8 py-4 bg-white text-gray-700 rounded-2xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg border border-gray-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Resources
            </button>
            <button
              onClick={onComplete}
              className={`inline-flex items-center px-8 py-4 rounded-2xl font-semibold transition-all duration-300 shadow-lg ${
                isCompleted
                  ? 'bg-[#02B491] text-white shadow-emerald-500/30'
                  : 'bg-[#02B491] text-white hover:bg-emerald-600 shadow-emerald-500/20'
              }`}
            >
              {isCompleted ? (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Completed
                </>
              ) : (
                'Mark as Complete'
              )}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Content */}
      <div className="w-full pb-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 space-y-16">
          
          <StepCard title="If you receive an offer: what to do next">
            <div className="space-y-8">
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
                <h5 className="font-bold text-gray-900 mb-4 text-lg">First things first:</h5>
                <p className="text-blue-800 leading-relaxed mb-4">
                  Even if you're unsure, always:
                </p>
                <div className="space-y-3">
                  {firstSteps.map((step, i) => (
                    <div key={i} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                      <span className="text-blue-800 leading-relaxed">{step}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </StepCard>

          <StepCard title="💰 How to approach salary & commission negotiations">
            <div className="space-y-8">
              <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6">
                <h5 className="font-bold text-gray-900 mb-3 text-lg flex items-center gap-2">
                  <Phone className="w-5 h-5 text-yellow-600" />
                  Important Note
                </h5>
                <p className="text-gray-700 leading-relaxed">
                  If you're going to negotiate, <span className="font-semibold">always try to do it via a call — not email</span>.
                </p>
              </div>
              
              <div>
                <h4 className="font-bold text-slate-900 mb-6 text-xl">Common reasons to negotiate:</h4>
                <div className="grid gap-4">
                  {negotiationReasons.map((reason, i) => (
                    <div key={i} className="flex items-start space-x-3 bg-white rounded-xl p-4 border border-gray-200">
                      <DollarSign className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-800 leading-relaxed">{reason}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-gray-200">
                <h5 className="font-bold text-gray-900 mb-4 text-lg">Suggested phrasing:</h5>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-gray-700 leading-relaxed italic">
                    "I'm really excited and keen to join — just wanted to ask whether there's any flexibility around the base or OTE, 
                    given [your reason]. Happy to jump on a quick call to discuss!"
                  </p>
                </div>
              </div>

              <div>
                <h5 className="font-bold text-gray-900 mb-4 text-lg">Make sure you clarify:</h5>
                <div className="grid gap-3">
                  {clarificationPoints.map((point, i) => (
                    <div key={i} className="flex items-start space-x-3 bg-blue-50 rounded-xl p-4 border border-blue-200">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                      <span className="text-blue-800 leading-relaxed">{point}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </StepCard>

          <StepCard title="⏰ Sequencing offers or buying yourself time">
            <div className="space-y-8">
              <p className="text-gray-600 text-lg leading-relaxed">
                If you're juggling offers or waiting on next steps:
              </p>
              
              <div className="grid gap-8 md:grid-cols-2">
                <div className="bg-white rounded-2xl p-6 border border-gray-200">
                  <h5 className="font-bold text-gray-900 mb-4 text-lg">To stall an offer you're still considering:</h5>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-gray-700 leading-relaxed italic text-sm">
                      "Thanks so much for the offer — I'm really excited. I'm just wrapping up conversations elsewhere 
                      and was wondering if I could have until [date] to confirm."
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-6 border border-gray-200">
                  <h5 className="font-bold text-gray-900 mb-4 text-lg">To speed up another company:</h5>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-gray-700 leading-relaxed italic text-sm">
                      "I've received an offer elsewhere but I'm genuinely excited by this opportunity. 
                      If there's any chance we can move things forward this week, I'd love to keep things open."
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </StepCard>

          <StepCard title="📋 Understanding your contract before signing">
            <div className="space-y-8">
              <p className="text-gray-600 text-lg leading-relaxed">
                Double check:
              </p>
              
              <div className="grid gap-3">
                {contractChecklist.map((item, i) => (
                  <div key={i} className="flex items-start space-x-3 bg-white rounded-xl p-4 border border-gray-200">
                    <FileText className="w-5 h-5 text-[#02B491] mt-1 flex-shrink-0" />
                    <span className="text-gray-800 leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>

              <div className="bg-green-50 border border-green-200 rounded-2xl p-6">
                <h5 className="font-bold text-gray-900 mb-3 text-lg">Need a second pair of eyes?</h5>
                <p className="text-gray-700 leading-relaxed">
                  <span className="font-semibold">Bethan</span> ({' '}
                  <a href="mailto:bethan@jumpstart-uk.com" className="text-[#02B491] hover:text-emerald-600 font-medium">
                    bethan@jumpstart-uk.com
                  </a>
                  ) can help review it, just reach out.
                </p>
              </div>
            </div>
          </StepCard>

          <StepCard title="📧 Following up if you haven't heard back">
            <div className="space-y-8">
              <p className="text-gray-600 text-lg leading-relaxed">
                If it's been:
              </p>
              
              <div className="grid gap-4 md:grid-cols-2">
                {followUpTimeline.map((item, i) => (
                  <div key={i} className="bg-white rounded-xl p-5 border border-gray-200">
                    <div className="flex items-center gap-3 mb-3">
                      <Clock className="w-5 h-5 text-[#02B491]" />
                      <span className="font-bold text-gray-900">{item.time}</span>
                    </div>
                    <p className="text-gray-700">{item.action}</p>
                  </div>
                ))}
              </div>

              <div className="bg-white rounded-2xl p-6 border border-gray-200">
                <h5 className="font-bold text-gray-900 mb-4 text-lg">Example follow-up email:</h5>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-gray-700 leading-relaxed italic">
                    "Hi [name], just checking in after our interview last [day] — I really enjoyed our conversation 
                    and wanted to see if there are any updates on next steps. Let me know if you need anything else from me!"
                  </p>
                </div>
              </div>
            </div>
          </StepCard>

          <StepCard title="🚪 How to withdraw from a process (at any stage)">
            <div className="space-y-8">
              <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
                <h5 className="font-bold text-gray-900 mb-3 text-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  Important Guidelines
                </h5>
                <div className="space-y-2">
                  {[
                    'Let Jumpstart know first at info@jumpstart-uk.com',
                    'Try to give at least 24 hours\' notice if an interview is already scheduled',
                    'Always be polite and professional — how you exit matters'
                  ].map((item, i) => (
                    <div key={i} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                      <span className="text-red-800 leading-relaxed">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="grid gap-8 md:grid-cols-2">
                <div className="bg-white rounded-2xl p-6 border border-gray-200">
                  <h5 className="font-bold text-gray-900 mb-4 text-lg">Example email (during interview process):</h5>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-gray-700 leading-relaxed italic text-sm">
                      "Hi [name], thank you again for the time and conversations so far. After reflecting on the opportunity, 
                      I've decided to focus my search elsewhere and would like to withdraw from the process. 
                      I really appreciate your time and hope to stay in touch."
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-6 border border-gray-200">
                  <h5 className="font-bold text-gray-900 mb-4 text-lg">Example email (post-offer — use to book a call):</h5>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-gray-700 leading-relaxed italic text-sm">
                      "Hi [name], I really appreciate the offer and all the time the team has invested in the process. 
                      I'd love to find a time to jump on a quick call to chat through where my head is at 
                      before making a final decision. Would [times] work?"
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
                <h5 className="font-bold text-gray-900 mb-3 text-lg">If in doubt we can help</h5>
                <p className="text-blue-800 leading-relaxed">
                  Just reach out to{' '}
                  <a href="mailto:info@jumpstart-uk.com" className="text-[#02B491] hover:text-emerald-600 font-medium">
                    info@jumpstart-uk.com
                  </a>
                </p>
              </div>
            </div>
          </StepCard>
        </div>

        {hasNext && (
          <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 pt-12">
            <div className="text-center">
              <button
                onClick={onNext}
                className="inline-flex items-center px-8 py-4 bg-white text-[#02B491] rounded-2xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg border-2 border-[#02B491] hover:shadow-emerald-500/20"
              >
                Continue to: {nextSectionTitle}
                <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesOffersEndgameSection; 